/**
 * How the roster and the map are arranged, per viewport (BON-538).
 *
 * `split` sets them side by side and pins the map, which stays in view while
 * the roster scrolls past it — the two are one answer and the map is the half
 * that does not scroll. It is entered only when the roster can still show every
 * column beside it; below that the map moves ABOVE the roster rather than the
 * roster giving up columns to keep the map company.
 *
 * A phone gets the roster alone: a map that small shows a handful of dots and
 * costs the screen the roster needs.
 *
 * The hovered venue lives HERE because it is the one piece of state the two
 * panels share — hovering a pin tints its row and hovering a row swells its
 * pin, which only works while both are reading one value.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { TableSort } from '../../primitives/useTableSort';

import { venueHref } from './venue-routes';
import { VenueMapCard } from './VenueMapCard';
import { VenueRosterCard } from './VenueRosterCard';
import type { VenueRosterRow } from './venues-roster-api';
import type { VenuesLayout } from './venues-viewport';

export interface VenuesBodyProps {
  venues: readonly VenueRosterRow[];
  search: string;
  onSearchChange: (value: string) => void;
  sort: TableSort<VenueRosterRow>;
  layout: VenuesLayout;
  tasksOnly: boolean;
  onTasksOnlyChange: (value: boolean) => void;
  regionScoped: boolean;
  /** The region scope, named. The map re-frames when this changes and not when
   *  the list merely narrows — typing in the search box must not move the
   *  camera out from under whoever is reading the map. */
  fitKey: string;
}

/** The stacked band's map height; the split layout's fills its column instead. */
const STACKED_MAP_HEIGHT = 340;

/** Room for the page header and padding above the pinned split-layout map. */
const DESKTOP_MAP_INSET = 160;

export function VenuesBody({
  venues,
  search,
  onSearchChange,
  sort,
  layout,
  tasksOnly,
  onTasksOnlyChange,
  regionScoped,
  fitKey,
}: VenuesBodyProps) {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const roster = (
    <VenueRosterCard
      venues={venues}
      search={search}
      onSearchChange={onSearchChange}
      sort={sort}
      layout={layout}
      tasksOnly={tasksOnly}
      onTasksOnlyChange={onTasksOnlyChange}
      regionScoped={regionScoped}
      hoveredId={hoveredId}
      onHoverVenue={setHoveredId}
    />
  );

  const map = (height: number | 'fill') => (
    <VenueMapCard
      venues={venues}
      fitKey={fitKey}
      height={height}
      hoveredId={hoveredId}
      onHoverVenue={setHoveredId}
      onSelectVenue={(id) => navigate(venueHref({ id }))}
    />
  );

  if (layout === 'split') {
    return (
      <div
        style={{
          display: 'grid',
          // 2fr/1fr — `VENUES_SPLIT_MIN_PX` is computed from this share.
          gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)',
          gap: 18,
          alignItems: 'start',
        }}
      >
        {roster}
        <div
          style={{
            position: 'sticky',
            top: 18,
            height: `calc(100vh - ${DESKTOP_MAP_INSET}px)`,
          }}
        >
          {map('fill')}
        </div>
      </div>
    );
  }

  if (layout === 'phone') return roster;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {map(STACKED_MAP_HEIGHT)}
      {roster}
    </div>
  );
}
